import { useMemo, useState } from 'react';
import { DataTable, Tabs, useIsMobile } from '../ui';
import { bonePositionStats, humanize, jointAngleStats, jointPositionStats } from '../../seriesExtractors';

const f = (dp) => (x) => (x == null || !Number.isFinite(x) ? '—' : x.toFixed(dp));
const f1 = f(1);
const f3 = f(3);

/**
 * Movement statistics — Joint DOF angles (results.jointsData), joint positions (from the
 * recorded skeleton frames) and bone positions (results.bonesData). Tabs without data are hidden;
 * the section disappears when none has data.
 */
export default function MovementStats({ results, skeleton }) {
    const mobile = useIsMobile();
    const dof = useMemo(() => jointAngleStats(results?.jointsData), [results]);
    const joints = useMemo(() => jointPositionStats(skeleton), [skeleton]);
    const bones = useMemo(() => bonePositionStats(results?.bonesData), [results]);

    const tabs = [
        dof.length && { value: 'dof', label: 'Joint DOF angles' },
        joints.length && { value: 'joint', label: 'Joint positions' },
        bones.length && { value: 'bone', label: 'Bone positions' },
    ].filter(Boolean);
    const [tab, setTab] = useState(null);
    if (!tabs.length) return null;
    const cur = tabs.some((t) => t.value === tab) ? tab : tabs[0].value;

    // On phones the first column is the card title, so fold the axis into it.
    const nameCol = (label) => (mobile
        ? { key: 'title', label, strong: true, render: (r) => `${r.label} · ${r.axis}` }
        : { key: 'label', label, w: '1.6fr', strong: true });

    let columns;
    let rows;
    let caption;
    if (cur === 'dof') {
        caption = 'Joint DOF angles';
        columns = [
            { key: 'label', label: 'Joint', w: '1.8fr', strong: true },
            { key: 'mn', label: 'Min °', align: 'right' },
            { key: 'mx', label: 'Max °', align: 'right' },
            { key: 'rom', label: 'ROM °', align: 'right', strong: true },
        ];
        rows = dof.map((r) => ({ id: r.name, label: humanize(r.name), mn: f1(r.min), mx: f1(r.max), rom: f1(r.rom) }));
    } else if (cur === 'joint') {
        caption = 'Joint position statistics';
        columns = [
            nameCol('Joint'),
            ...(mobile ? [] : [{ key: 'axis', label: 'Axis', w: '.6fr', muted: true }]),
            { key: 'mn', label: 'Min', align: 'right' },
            { key: 'mx', label: 'Max', align: 'right' },
            { key: 'me', label: 'Mean', align: 'right' },
        ];
        rows = joints.map((r) => ({ id: r.name + r.axis, label: humanize(r.name), axis: r.axis, mn: f3(r.min), mx: f3(r.max), me: f3(r.mean) }));
    } else {
        caption = 'Bone position statistics';
        columns = [
            nameCol('Bone'),
            ...(mobile ? [] : [{ key: 'axis', label: 'Axis', w: '.6fr', muted: true }]),
            { key: 'mn', label: 'Min', align: 'right' },
            { key: 'mx', label: 'Max', align: 'right' },
            { key: 'rg', label: 'Range', align: 'right', strong: true },
        ];
        rows = bones.map((r) => ({ id: r.name + r.axis, label: humanize(r.name), axis: r.axis, mn: f3(r.min), mx: f3(r.max), rg: f3(r.range) }));
    }

    return (
        <section aria-labelledby="sd-stats-h" className="sd-stats">
            <h2 id="sd-stats-h" className="eg-h2">Movement statistics</h2>
            <div className="sd-stats__tabs">
                <Tabs tabs={tabs} value={cur} onChange={setTab} label="Statistics" />
            </div>
            <DataTable columns={columns} rows={rows} caption={caption} dense getKey={(r) => r.id} />
            {cur === 'joint' ? <span className="eg-small eg-secondary sd-stats__note">Tracker units</span> : null}
        </section>
    );
}
